import { useState, useEffect } from 'react';

/**
 * Hook personalizado para aplicar debounce a valores
 * @param {*} value - Valor al cual aplicar debounce
 * @param {number} delay - Tiempo de espera en milisegundos (default: 500ms)
 * @returns {*} Valor con debounce aplicado
 */
export const useDebounce = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    // Crear un timer que actualiza el valor después del delay
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Limpiar el timer si el valor cambia antes del delay
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Hook personalizado para manejar callbacks con debounce
 * @param {Function} callback - Función a ejecutar con debounce
 * @param {number} delay - Tiempo de espera en milisegundos (default: 500ms)
 * @returns {Function} Función con debounce aplicado
 */
export const useDebouncedCallback = (callback, delay = 500) => {
  const [debounceTimer, setDebounceTimer] = useState(null);

  const debouncedFunction = (...args) => {
    // Limpiar el timer anterior si existe
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Crear nuevo timer
    const newTimer = setTimeout(() => {
      callback(...args);
    }, delay);

    setDebounceTimer(newTimer);
  };

  // Limpiar timer cuando el componente se desmonte
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  return debouncedFunction;
};
